import React, { useEffect } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { Descriptions, Modal, Spin, Typography } from "antd";

import { retrieveFetchRequest } from "../../../store/clicks/actions";
import { Deeplink } from "../../../store/common_types";
import { RootState } from "../../../store/root_reducer";
import { formatNumber } from "../../../utils/table";
import { DeeplinkComponent } from "../../deeplink";

import styles from "./styles.module.scss";

const { Title, Text } = Typography;

export interface ClickModalProps {
  marker?: string;
  visible: boolean;
  onDeeplinkClick?: (deeplink: Deeplink) => void;
  onClose: () => void;
}

export const ClickModal: React.FC<ClickModalProps> = (props) => {
  const { t } = useTranslation("click_modal");

  const selectedGate = useSelector((state: RootState) => state.User.selectedGate);
  const selectedCurrency = useSelector((state: RootState) => state.Currencies.selectedCurrency);
  const isLoading = useSelector((state: RootState) => state.Clicks.retrieveIsLoading);
  const click = useSelector((state: RootState) => state.Clicks.retrieve);

  const dispatch = useDispatch();
  useEffect(() => {
    if (props.visible && props.marker) {
      dispatch(retrieveFetchRequest({ marker: props.marker, gateId: selectedGate.id, currency: selectedCurrency }));
    }
  }, [dispatch, props.visible, props.marker, selectedGate, selectedCurrency]);

  return (
    <Modal
      visible={props.visible}
      width="600px"
      title={
        <Title level={4}>
          {" "}
          {t("title")}
          <Text code>{props.marker}</Text>
        </Title>
      }
      onCancel={props.onClose}
      footer={[]}
    >
      <Spin size="large" spinning={isLoading}>
        <Descriptions className={styles.descriptions} bordered size="small" column={1}>
          <Descriptions.Item label={t("content.createdAt.title")}>{click?.createdAt}</Descriptions.Item>
          <Descriptions.Item label={t("content.marker.title")}>{click?.marker}</Descriptions.Item>
          <Descriptions.Item label={t("content.prediction.title")}>
            {click && formatNumber(click.prediction || 0)}
          </Descriptions.Item>
          <Descriptions.Item label={t("content.proposalPrice.title")}>
            {click && `${formatNumber(click.proposalPrice)} ${selectedCurrency}`}
          </Descriptions.Item>
          <Descriptions.Item label={t("content.gatePrice.title")}>
            {click && `${formatNumber(click.gatePriceAmount)} ${click.gatePriceCurrency.toUpperCase()}`}
          </Descriptions.Item>
          <Descriptions.Item label={t("content.platform.title")}>
            {click && t(`common:platforms.${click.platform}`)}
          </Descriptions.Item>
          <Descriptions.Item label={t("content.referrerHost.title")}>{click?.referrerHost}</Descriptions.Item>
          <Descriptions.Item label={t("content.searchUserIp.title")}>{click?.searchUserIp}</Descriptions.Item>
          <Descriptions.Item label={t("content.market.title")}>{click?.market?.toUpperCase()}</Descriptions.Item>
          <Descriptions.Item label={t("content.mainAirline.title")}>{click?.mainAirline}</Descriptions.Item>
          <Descriptions.Item label={t("content.from.title")}>
            {click && `${click.origin0}${click.origin1 ? `, ${click.origin1}` : ""}`}
          </Descriptions.Item>
          <Descriptions.Item label={t("content.to.title")}>
            {click && `${click.destination0}${click.destination1 ? `, ${click.destination1}` : ""}`}
          </Descriptions.Item>
          <Descriptions.Item label={t("content.isMixedFlights.title")}>
            {click && t(`content.isMixedFlights.answers.${click.isMixedFlights ? "yes" : "no"}`)}
          </Descriptions.Item>
          <Descriptions.Item label={t("content.isUnique.title")}>
            {click && t(`content.isUnique.answers.${click.isUnique ? "yes" : "no"}`)}
          </Descriptions.Item>
          <Descriptions.Item label={t("content.deeplink.title")}>
            {click && click.deeplink && (
              <DeeplinkComponent
                className={styles.modalDeeplink}
                deeplink={click.deeplink}
                visibleTooltip={false}
                onDeeplinkClick={props.onDeeplinkClick}
              />
            )}
          </Descriptions.Item>
        </Descriptions>
      </Spin>
    </Modal>
  );
};
